
import React, { useEffect, useRef } from 'react';
import { useCookieConsent } from '@/contexts/CookieConsentContext';

interface AdSenseAdProps {
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'vertical' | 'horizontal';
  style?: React.CSSProperties;
  className?: string;
  responsive?: boolean;
  autoAd?: boolean; // New property for auto ads
}

export const AdSenseAd = ({ 
  slot, 
  format = 'auto',
  style = {}, 
  className = '',
  responsive = true,
  autoAd = false // Default to false for backward compatibility
}: AdSenseAdProps) => {
  const adRef = useRef<HTMLDivElement>(null);
  const { hasConsent } = useCookieConsent();

  useEffect(() => {
    // Skip ad rendering in development mode
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    
    try {
      // Wait for AdSense to be initialized
      if (window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error('Error initializing AdSense ad:', e);
    }
  }, []);

  const isPersonalized = hasConsent('advertising');

  // In development mode, show placeholder
  if (process.env.NODE_ENV === 'development') {
    return (
      <div 
        className={`adsense-placeholder ${className}`}
        style={{
          backgroundColor: 'rgba(0, 0, 255, 0.1)',
          border: '1px dashed #999',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100px',
          padding: '10px',
          boxSizing: 'border-box',
          ...style
        }}
      >
        {autoAd ? 'AdSense Auto Ad' : `AdSense Ad (Slot: ${slot})`}
      </div>
    );
  }

  // For auto ads, we don't need to render an ins element
  if (autoAd) {
    return null;
  }

  return (
    <div className={className} ref={adRef}>
      {!isPersonalized && (
        <div className="text-xs text-muted-foreground mb-1">Non-personalized ad</div>
      )}
      <ins
        className="adsbygoogle"
        style={style}
        data-ad-client="ca-pub-2729367668902676"
        data-ad-slot={slot}
        data-ad-format={format}
        {...(responsive ? { "data-full-width-responsive": "true" } : {})}
        {...(!isPersonalized ? { "data-npa": "1" } : {})}
      />
    </div>
  );
};

// Declare global adsbygoogle for TypeScript
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}
